import { useEffect, useState } from "react";
import axios from "axios";
import { Gift, History, Loader2, Medal, Save, Trophy } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const tiers = [
  { key: "silver", label: "Silver", accent: "border-slate-300 bg-slate-50", iconColor: "text-slate-500" },
  { key: "gold", label: "Gold", accent: "border-amber-300 bg-amber-50", iconColor: "text-amber-500" },
  { key: "platinum", label: "Platinum", accent: "border-cyan-300 bg-cyan-50", iconColor: "text-cyan-600" },
];

const formatActivityTime = (value) => {
  const date = new Date(value);
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const dayDifference = Math.round((startOfToday - startOfDate) / 86400000);
  const time = date.toLocaleTimeString("en-IN", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }).replace(":", ".");
  if (dayDifference === 0) return `Today at ${time}`;
  if (dayDifference === 1) return `Yesterday at ${time}`;
  return `${date.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })} at ${time}`;
};

const RewardsPage = () => {
  const navigate = useNavigate();
  const [loyaltyPoints, setLoyaltyPoints] = useState({ silver: 0, gold: 0, platinum: 0 });
  const [minimumReferrals, setMinimumReferrals] = useState({ silver: 0, gold: 15, platinum: 25 });
  const [maxPointsPerOrder, setMaxPointsPerOrder] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [overview, setOverview] = useState({ timeline: [], leaderboard: [] });

  const authHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem("gasman_token")}` },
  });

  useEffect(() => {
    const fetchRewards = async () => {
      try {
        const [response, overviewResponse] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/admin/settings`, authHeaders()),
          axios.get(`${BACKEND_URL}/api/admin/rewards/overview`, authHeaders()),
        ]);
        setLoyaltyPoints({
          silver: response.data.silver_loyalty_points_per_order ?? 0,
          gold: response.data.gold_loyalty_points_per_order ?? 0,
          platinum: response.data.platinum_loyalty_points_per_order ?? 0,
        });
        setMinimumReferrals({
          silver: response.data.silver_minimum_successful_referrals ?? 0,
          gold: response.data.gold_minimum_successful_referrals ?? 15,
          platinum: response.data.platinum_minimum_successful_referrals ?? 25,
        });
        setMaxPointsPerOrder(response.data.max_loyalty_points_per_order ?? 0);
        setOverview(overviewResponse.data);
      } catch {
        toast.error("Failed to load rewards page");
      } finally {
        setLoading(false);
      }
    };

    fetchRewards();
  }, []);

  const handleSave = async () => {
    const values = Object.values(loyaltyPoints).map(Number);
    if (values.some((value) => !Number.isInteger(value) || value < 0)) {
      toast.error("Loyalty points must be whole numbers of zero or more");
      return;
    }
    const referralValues = Object.values(minimumReferrals).map(Number);
    if (referralValues.some((value) => !Number.isInteger(value) || value < 0)) {
      toast.error("Minimum referrals must be whole numbers of zero or more");
      return;
    }
    if (referralValues[0] !== 0 || !(referralValues[0] < referralValues[1] && referralValues[1] < referralValues[2])) {
      toast.error("Silver must start at 0 and referral requirements must increase for each tier");
      return;
    }
    if (!Number.isInteger(Number(maxPointsPerOrder)) || Number(maxPointsPerOrder) < 0) {
      toast.error("Maximum usable loyalty points must be a whole number of zero or more");
      return;
    }

    setSaving(true);
    try {
      await axios.put(
        `${BACKEND_URL}/api/admin/settings`,
        {
          silver_loyalty_points_per_order: Number(loyaltyPoints.silver),
          gold_loyalty_points_per_order: Number(loyaltyPoints.gold),
          platinum_loyalty_points_per_order: Number(loyaltyPoints.platinum),
          silver_minimum_successful_referrals: Number(minimumReferrals.silver),
          gold_minimum_successful_referrals: Number(minimumReferrals.gold),
          platinum_minimum_successful_referrals: Number(minimumReferrals.platinum),
          max_loyalty_points_per_order: Number(maxPointsPerOrder),
        },
        authHeaders()
      );
      toast.success("Reward settings saved!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save reward settings");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="animate-spin text-[#0A4CAE]" size={32} />
      </div>
    );
  }

  return (
    <div data-testid="rewards-page">
      <div className="mb-8 flex items-center gap-3">
        <div className="rounded-lg bg-[#e8eef9] p-2">
          <Gift size={22} className="text-[#0A4CAE]" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Rewards</h1>
          <p className="mt-0.5 text-sm text-gray-500">Manage loyalty tiers and reward points.</p>
        </div>
      </div>

      <div className="mb-6 grid grid-cols-1 gap-6 xl:grid-cols-2">
        <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
          <div className="border-b border-gray-100 p-5">
            <h2 className="flex items-center gap-2 text-base font-semibold text-gray-800">
              <History size={17} className="text-[#0A4CAE]" />
              Rewards Timeline
            </h2>
            <p className="mt-1 text-sm text-gray-500">Latest referral and loyalty reward activity.</p>
          </div>
          <div className="divide-y divide-gray-100 px-5">
            {overview.timeline.length === 0 ? (
              <p className="py-10 text-center text-sm text-gray-500">No referral activity yet.</p>
            ) : overview.timeline.map((event, index) => (
              <div key={`${event.type}-${event.created_at}-${index}`} className="py-4">
                <p className="text-sm leading-6 text-gray-700">
                  <button
                    type="button"
                    onClick={() => navigate(`/admin/users/${event.referrer_user_id}`)}
                    className="font-semibold text-[#0A4CAE] hover:underline"
                  >
                    {event.referrer_name}
                  </button>
                  {event.type === "referral_signup" ? (
                    <>
                      {" referred "}
                      <button
                        type="button"
                        onClick={() => navigate(`/admin/users/${event.referred_user_id}`)}
                        className="font-semibold text-[#0A4CAE] hover:underline"
                      >
                        {event.referred_user_name}
                      </button>
                    </>
                  ) : (
                    <>
                      {` earned ${event.points} loyalty points on `}
                      <button
                        type="button"
                        onClick={() => navigate(`/admin/users/${event.referred_user_id}`)}
                        className="font-semibold text-[#0A4CAE] hover:underline"
                      >
                        {event.referred_user_name}'s
                      </button>
                      {" order "}
                      <button
                        type="button"
                        onClick={() => navigate(`/admin/orders/${event.order_id}`)}
                        className="font-mono font-semibold text-[#0A4CAE] hover:underline"
                      >
                        #{event.order_id?.slice(0, 8)}
                      </button>
                    </>
                  )}
                </p>
                <p className="mt-1 text-xs text-gray-400">{formatActivityTime(event.created_at)}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
          <div className="border-b border-gray-100 p-5">
            <h2 className="flex items-center gap-2 text-base font-semibold text-gray-800">
              <Medal size={17} className="text-amber-500" />
              Referral Leaderboard
            </h2>
            <p className="mt-1 text-sm text-gray-500">Top users by successful referrals.</p>
          </div>
          <div className="divide-y divide-gray-100 px-5">
            {overview.leaderboard.length === 0 ? (
              <p className="py-10 text-center text-sm text-gray-500">No successful referrals yet.</p>
            ) : overview.leaderboard.map((entry) => (
              <div key={entry.user_id} className="flex items-center gap-4 py-3.5">
                <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-bold ${
                  entry.rank === 1 ? "bg-amber-100 text-amber-700" :
                  entry.rank === 2 ? "bg-slate-200 text-slate-700" :
                  entry.rank === 3 ? "bg-orange-100 text-orange-700" : "bg-gray-100 text-gray-600"
                }`}>
                  {entry.rank}
                </div>
                <button
                  type="button"
                  onClick={() => navigate(`/admin/users/${entry.user_id}`)}
                  className="flex-1 text-left font-semibold text-gray-800 hover:text-[#0A4CAE] hover:underline"
                >
                  {entry.user_name}
                </button>
                <div className="text-right">
                  <p className="text-lg font-bold text-[#0A4CAE]">{entry.successful_referrals}</p>
                  <p className="text-xs text-gray-500">successful referrals</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
        <div className="border-b border-gray-100 p-6">
          <h2 className="flex items-center gap-2 text-base font-semibold text-gray-800">
            <Trophy size={17} className="text-amber-500" />
            Loyalty Tier Rules
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            Configure points earned per order and the successful referrals required to enter each tier.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-4 p-6 md:grid-cols-3">
          {tiers.map((tier) => (
            <div key={tier.key} className={`rounded-xl border p-5 ${tier.accent}`}>
              <div className="mb-4 flex items-center gap-2">
                <Trophy size={18} className={tier.iconColor} />
                <Label htmlFor={`${tier.key}_loyalty_points`} className="text-sm font-bold text-gray-800">
                  {tier.label}
                </Label>
              </div>
              <Input
                id={`${tier.key}_loyalty_points`}
                type="number"
                min="0"
                step="1"
                value={loyaltyPoints[tier.key]}
                onChange={(event) => setLoyaltyPoints((current) => ({
                  ...current,
                  [tier.key]: event.target.value,
                }))}
                className="bg-white"
              />
              <p className="mt-2 text-xs font-medium text-gray-500">Points earned per eligible order</p>
              <Label htmlFor={`${tier.key}_minimum_referrals`} className="mt-5 block text-xs font-semibold text-gray-700">
                Minimum successful referrals
              </Label>
              <Input
                id={`${tier.key}_minimum_referrals`}
                type="number"
                min="0"
                step="1"
                value={minimumReferrals[tier.key]}
                disabled={tier.key === "silver"}
                onChange={(event) => setMinimumReferrals((current) => ({
                  ...current,
                  [tier.key]: event.target.value,
                }))}
                className="mt-2 bg-white disabled:opacity-70"
              />
              <p className="mt-2 text-xs font-medium text-gray-500">
                A referral becomes successful after their first completed booking.
              </p>
            </div>
          ))}
        </div>

        <div className="border-t border-gray-100 px-6 py-5">
          <div className="max-w-sm">
            <Label htmlFor="max_loyalty_points_per_order" className="text-sm font-semibold text-gray-800">
              Maximum loyalty points usable per order
            </Label>
            <Input
              id="max_loyalty_points_per_order"
              type="number"
              min="0"
              step="1"
              value={maxPointsPerOrder}
              onChange={(event) => setMaxPointsPerOrder(event.target.value)}
              className="mt-2 bg-white"
            />
            <p className="mt-2 text-xs text-gray-500">One loyalty point equals ₹1. Set 0 to disable redemption.</p>
          </div>
        </div>

        <div className="px-6 pb-6">
          <Button
            onClick={handleSave}
            disabled={saving}
            className="gap-2 bg-[#0A4CAE] text-white hover:bg-[#083d8a]"
          >
            {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />}
            {saving ? "Saving…" : "Save Reward Settings"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default RewardsPage;
